import { TranslationApi } from './translation-api';
import {
  TranslationKey,
  TranslationsResponse,
  LanguagesResponse,
  CreateTranslationRequest,
  UpdateTranslationRequest,
  TranslationQueryParams,
  ApplicationType,
  BulkImportValidateResponse,
  BulkImportResult,
  ImportMode,
  PublishStatus,
  PublishResult,
  TranslationVersion,
  VersionsResponse,
  S3Environment,
  VersionQueryParams,
  RollbackResult,
  ExportResponse,
  PreviewInfoResponse,
  CopyLanguageValuesResult,
} from '../types/translation-types';

export class TranslationRepository {
  constructor(private readonly api: TranslationApi) {}

  async getTranslations(params: TranslationQueryParams): Promise<TranslationsResponse> {
    try {
      return await this.api.getTranslations(params);
    } catch (error) {
      console.error('TranslationRepository.getTranslations error:', error);
      throw error;
    }
  }

  async getById(id: number): Promise<TranslationKey> {
    try {
      return await this.api.getById(id);
    } catch (error) {
      console.error(`TranslationRepository.getById(${id}) error:`, error);
      throw error;
    }
  }

  async create(data: CreateTranslationRequest): Promise<TranslationKey> {
    try {
      return await this.api.create(data);
    } catch (error) {
      console.error('TranslationRepository.create error:', error);
      throw error;
    }
  }

  async update(id: number, data: UpdateTranslationRequest): Promise<TranslationKey> {
    try {
      return await this.api.update(id, data);
    } catch (error) {
      console.error(`TranslationRepository.update(${id}) error:`, error);
      throw error;
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await this.api.delete(id);
    } catch (error) {
      console.error(`TranslationRepository.delete(${id}) error:`, error);
      throw error;
    }
  }

  async copyLanguageValues(
    newLanguageCode: string,
    baseLanguageCode: string
  ): Promise<CopyLanguageValuesResult> {
    try {
      return await this.api.copyLanguageValues(newLanguageCode, baseLanguageCode);
    } catch (error) {
      console.error(
        `TranslationRepository.copyLanguageValues(${newLanguageCode}, ${baseLanguageCode}) error:`,
        error
      );
      throw error;
    }
  }

  async getLanguages(): Promise<LanguagesResponse> {
    try {
      return await this.api.getLanguages();
    } catch (error) {
      console.error('TranslationRepository.getLanguages error:', error);
      throw error;
    }
  }

  // Bulk Import Methods

  async validateBulkImport(
    applicationType: ApplicationType,
    file: File
  ): Promise<BulkImportValidateResponse> {
    try {
      return await this.api.validateBulkImport(applicationType, file);
    } catch (error) {
      console.error('TranslationRepository.validateBulkImport error:', error);
      throw error;
    }
  }

  async bulkImport(
    applicationType: ApplicationType,
    file: File,
    mode: ImportMode
  ): Promise<BulkImportResult> {
    try {
      return await this.api.bulkImport(applicationType, file, mode);
    } catch (error) {
      console.error('TranslationRepository.bulkImport error:', error);
      throw error;
    }
  }

  async downloadTemplate(applicationType: ApplicationType): Promise<ExportResponse> {
    try {
      return await this.api.downloadTemplate(applicationType);
    } catch (error) {
      console.error('TranslationRepository.downloadTemplate error:', error);
      throw error;
    }
  }

  async exportTranslations(applicationType: ApplicationType): Promise<ExportResponse> {
    try {
      return await this.api.exportTranslations(applicationType);
    } catch (error) {
      console.error('TranslationRepository.exportTranslations error:', error);
      throw error;
    }
  }

  // Publish & Version Methods

  async getPublishStatus(applicationType: ApplicationType): Promise<PublishStatus> {
    try {
      return await this.api.getPublishStatus(applicationType);
    } catch (error) {
      console.error('TranslationRepository.getPublishStatus error:', error);
      throw error;
    }
  }

  async getPublishPreview(applicationType: ApplicationType): Promise<string> {
    try {
      return await this.api.getPublishPreview(applicationType);
    } catch (error) {
      console.error('TranslationRepository.getPublishPreview error:', error);
      throw error;
    }
  }

  async publish(
    applicationType: ApplicationType,
    environment: S3Environment,
    notes?: string
  ): Promise<PublishResult> {
    try {
      return await this.api.publish(applicationType, environment, notes);
    } catch (error) {
      console.error('TranslationRepository.publish error:', error);
      throw error;
    }
  }

  async getVersions(params: VersionQueryParams): Promise<VersionsResponse> {
    try {
      return await this.api.getVersions(params);
    } catch (error) {
      console.error('TranslationRepository.getVersions error:', error);
      throw error;
    }
  }

  async getVersionById(versionId: number): Promise<TranslationVersion> {
    try {
      return await this.api.getVersionById(versionId);
    } catch (error) {
      console.error(`TranslationRepository.getVersionById(${versionId}) error:`, error);
      throw error;
    }
  }

  async rollbackVersion(versionId: number, reason: string): Promise<RollbackResult> {
    try {
      return await this.api.rollbackVersion(versionId, reason);
    } catch (error) {
      console.error(`TranslationRepository.rollbackVersion(${versionId}) error:`, error);
      throw error;
    }
  }

  // Preview Methods

  async getPreviewInfo(applicationType: ApplicationType): Promise<PreviewInfoResponse> {
    try {
      return await this.api.getPreviewInfo(applicationType);
    } catch (error) {
      console.error('TranslationRepository.getPreviewInfo error:', error);
      throw error;
    }
  }

  async getPreviewContent(applicationType: ApplicationType, fileKey?: string): Promise<string> {
    try {
      return await this.api.getPreviewContent(applicationType, fileKey);
    } catch (error) {
      console.error('TranslationRepository.getPreviewContent error:', error);
      throw error;
    }
  }

  getPreviewDownloadUrl(applicationType: ApplicationType, fileKey?: string): string {
    return this.api.getPreviewDownloadUrl(applicationType, fileKey);
  }

  async getDownloadHeaders(): Promise<Record<string, string>> {
    return this.api.getDownloadHeaders();
  }
}
